import { PreviewFeature } from 'features/Modules/Preview';
import React, { useContext } from 'react';

export class PageContextProps {
	private _pageContent: Content.PageContent;

	public constructor(pageContent: Content.PageContent) {
		this._pageContent = pageContent;
	}

	public get breadcrumb(): Content.PageContent['breadcrumb'] {
		return this._pageContent?.breadcrumb;
	}

	public get chatbotConfiguration(): Vendors.ChatbotConfig {
		return this._pageContent?.chatbotConfig;
	}

	public get childPages(): Content.PageContent['page']['childPages'] {
		return this._pageContent?.page?.childPages;
	}

	public get configuration(): Content.PageContent['configuration'] {
		return this._pageContent?.configuration;
	}

	public get content(): Content.Page['properties'] {
		return this._pageContent?.page?.properties;
	}

	public get createDate() {
		return this._pageContent?.page?.createDate;
	}

	public get documentType(): string {
		return this._pageContent?.page?.documentType;
	}

	public get footer(): Content.PageContent['footer'] {
		return this._pageContent?.footer;
	}

	public get host(): Content.PageContent['host'] {
		return this._pageContent?.host;
	}

	public get id(): string {
		return this._pageContent?.page?.id;
	}

	public get lastUpdated() {
		return this._pageContent?.page?.lastUpdated;
	}

	public get moduleMarginBottom(): string {
		return this._pageContent?.page?.properties.moduleMarginBottom;
	}

	public get moduleMarginBottomAside(): string {
		return this._pageContent?.page?.properties.moduleMarginBottomAside;
	}

	public get name(): string {
		return this._pageContent?.page?.name;
	}

	public get pageContent(): Content.PageContent {
		return this._pageContent;
	}

	public get pageLanguageVersions(): Models.PageLanguageVersion[] {
		return this._pageContent?.page?.pageLanguageVersions;
	}

	public get parent(): { id: string; name: string } {
		return this._pageContent?.page?.parent;
	}

	public get preview(): boolean {
		return this._pageContent?.preview ?? false;
	}

	public get previewData(): Models.PreviewData {
		return this._pageContent?.previewData;
	}

	public get segment(): Content.PageContent['segment'] {
		return this._pageContent?.segment;
	}

	public get template(): string {
		return this._pageContent?.page?.template;
	}

	public get transportLinksInOtherView(): boolean {
		return this._pageContent.page.properties.pageTransportLinksInOtherView;
	}

	public get transportLinkHide(): boolean {
		return this._pageContent.page.properties.pageTransportLinksHide;
	}

	public get url(): string {
		return this._pageContent?.page?.url;
	}
}

export const PageContext = React.createContext<Partial<PageContextProps>>(new PageContextProps(null));

export interface PageContextProviderProps {
	content: Content.PageContent;
	host: string;
	children?: React.ReactNode;
}
export const PageContextProvider: React.FC<PageContextProviderProps> = ({ content, host, children }) => {
	content ? (content.host = host) : content;
	const pageContext: PageContextProps = new PageContextProps(content);
	return (
		<PageContext.Provider value={pageContext}>
			<PreviewFeature url={content?.page?.url}>{children}</PreviewFeature>
		</PageContext.Provider>
	);
};

export function usePageContext(): Partial<PageContextProps> {
	return useContext(PageContext);
}
